import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { OperatorsUser } from "./operators";
import type { OperatorsAuthSession } from "./operatorsAuthSession";
import { resetNativeApi } from "./nativeApi";
import {
  disableGoogleAutoSignIn,
  rememberGoogleLoginEmail,
  setGoogleAutoSignInSuppressed,
} from "./googleSignIn";
import {
  createDevSession,
  exchangeGoogleIdToken,
  fetchCurrentOperatorsSession,
  logoutOperator,
  notifyOperatorsAuthSessionRefreshed,
  onOperatorsAuthExpired,
  refreshOperatorsSession,
} from "./operatorsAuthSession";

type OperatorsAuthStatus = "checking" | "signedIn" | "signedOut" | "signingIn";

type OperatorsAuthContextValue = {
  error: string | null;
  expiresAt: string | null;
  logout: () => Promise<void>;
  signInWithDevSession: () => Promise<void>;
  signInWithGoogleIdToken: (idToken: string) => Promise<void>;
  status: OperatorsAuthStatus;
  user: OperatorsUser | null;
};

const OperatorsAuthContext = createContext<OperatorsAuthContextValue | null>(null);

const SESSION_REFRESH_SKEW_MS = 2 * 60_000;
const SESSION_REFRESH_RETRY_MS = 30_000;
const SESSION_REFRESH_RECENT_ACTIVITY_MS = 30 * 60_000;
const MIN_SESSION_REFRESH_DELAY_MS = 5_000;

function millisecondsUntil(isoDate: string | null): number | null {
  if (!isoDate) {
    return null;
  }

  const expiresAt = Date.parse(isoDate);
  return Number.isFinite(expiresAt) ? expiresAt - Date.now() : null;
}

export function OperatorsAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<OperatorsAuthStatus>("checking");
  const [user, setUser] = useState<OperatorsUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);
  const [refreshRetryNonce, setRefreshRetryNonce] = useState(0);
  const lastActivityAtRef = useRef(Date.now());
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (user) {
      rememberGoogleLoginEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const session = await fetchCurrentOperatorsSession({ notifyOnUnauthorized: false });
        if (cancelled) {
          return;
        }

        setUser(session?.user ?? null);
        setSessionExpiresAt(session?.expiresAt ?? null);
        setStatus(session ? "signedIn" : "signedOut");
        if (session) {
          notifyOperatorsAuthSessionRefreshed(session);
        }
      } catch (authError) {
        if (cancelled) {
          return;
        }

        setError(
          authError instanceof Error
            ? authError.message
            : "Could not restore the current Operators session.",
        );
        setUser(null);
        setSessionExpiresAt(null);
        setStatus("signedOut");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () =>
      onOperatorsAuthExpired((message) => {
        resetNativeApi();
        setError(message);
        setUser(null);
        setSessionExpiresAt(null);
        setStatus("signedOut");
      }),
    [],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const markActive = () => {
      lastActivityAtRef.current = Date.now();
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      "focus",
      "keydown",
      "mousedown",
      "pointerdown",
      "touchstart",
    ];
    for (const eventName of activityEvents) {
      window.addEventListener(eventName, markActive, { passive: true });
    }
    document.addEventListener("visibilitychange", markActive);

    return () => {
      for (const eventName of activityEvents) {
        window.removeEventListener(eventName, markActive);
      }
      document.removeEventListener("visibilitychange", markActive);
    };
  }, []);

  const refreshSessionIfActive = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return await refreshInFlightRef.current;
    }

    const refreshPromise = (async () => {
      const session = await refreshOperatorsSession();
      setError(null);
      setUser(session.user);
      setSessionExpiresAt(session.expiresAt);
      setStatus("signedIn");
    })();
    refreshInFlightRef.current = refreshPromise;
    try {
      await refreshPromise;
    } finally {
      if (refreshInFlightRef.current === refreshPromise) {
        refreshInFlightRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (status !== "signedIn" || !sessionExpiresAt) {
      return;
    }

    const millisecondsToExpiry = millisecondsUntil(sessionExpiresAt);
    if (millisecondsToExpiry === null || millisecondsToExpiry <= 0) {
      resetNativeApi();
      setUser(null);
      setSessionExpiresAt(null);
      setStatus("signedOut");
      return;
    }

    const refreshDelay =
      millisecondsToExpiry <= SESSION_REFRESH_SKEW_MS
        ? Math.min(
            SESSION_REFRESH_RETRY_MS,
            Math.max(MIN_SESSION_REFRESH_DELAY_MS, millisecondsToExpiry),
          )
        : Math.max(MIN_SESSION_REFRESH_DELAY_MS, millisecondsToExpiry - SESSION_REFRESH_SKEW_MS);
    const timeout = window.setTimeout(() => {
      const recentlyActive =
        Date.now() - lastActivityAtRef.current <= SESSION_REFRESH_RECENT_ACTIVITY_MS;
      const visible = document.visibilityState !== "hidden";

      if (!recentlyActive || !visible) {
        setRefreshRetryNonce((current) => current + 1);
        return;
      }

      void refreshSessionIfActive().catch((authError) => {
        setError(
          authError instanceof Error
            ? authError.message
            : "Could not refresh the Operators session.",
        );
        setRefreshRetryNonce((current) => current + 1);
      });
    }, refreshDelay);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [refreshRetryNonce, refreshSessionIfActive, sessionExpiresAt, status]);

  useEffect(() => {
    if (status !== "signedIn" || !sessionExpiresAt || typeof window === "undefined") {
      return;
    }

    const handleActivity = () => {
      const millisecondsToExpiry = millisecondsUntil(sessionExpiresAt);
      if (millisecondsToExpiry === null || millisecondsToExpiry <= 0) {
        resetNativeApi();
        setUser(null);
        setSessionExpiresAt(null);
        setStatus("signedOut");
        return;
      }

      if (millisecondsToExpiry > SESSION_REFRESH_SKEW_MS) {
        return;
      }

      void refreshSessionIfActive().catch((authError) => {
        setError(
          authError instanceof Error
            ? authError.message
            : "Could not refresh the Operators session.",
        );
        setRefreshRetryNonce((current) => current + 1);
      });
    };

    window.addEventListener("focus", handleActivity);
    window.addEventListener("pointerdown", handleActivity, { passive: true });
    window.addEventListener("keydown", handleActivity);

    return () => {
      window.removeEventListener("focus", handleActivity);
      window.removeEventListener("pointerdown", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
  }, [refreshSessionIfActive, sessionExpiresAt, status]);

  const applySignIn = useCallback(async (loadSession: () => Promise<OperatorsAuthSession>) => {
    setError(null);
    setStatus("signingIn");

    try {
      const session = await loadSession();
      setGoogleAutoSignInSuppressed(false);

      resetNativeApi();
      setUser(session.user);
      setSessionExpiresAt(session.expiresAt);
      setStatus("signedIn");
      notifyOperatorsAuthSessionRefreshed(session);
    } catch (authError) {
      resetNativeApi();
      setUser(null);
      setSessionExpiresAt(null);
      setStatus("signedOut");
      setError(authError instanceof Error ? authError.message : "Google sign-in failed.");
      throw authError;
    }
  }, []);

  const signInWithGoogleIdToken = useCallback(
    async (idToken: string) => {
      await applySignIn(() => exchangeGoogleIdToken(idToken));
    },
    [applySignIn],
  );

  const signInWithDevSession = useCallback(async () => {
    await applySignIn(createDevSession);
  }, [applySignIn]);

  const logout = useCallback(async () => {
    disableGoogleAutoSignIn();
    try {
      await logoutOperator();
    } finally {
      resetNativeApi();
      setError(null);
      setUser(null);
      setSessionExpiresAt(null);
      setStatus("signedOut");
      notifyOperatorsAuthSessionRefreshed(null);
    }
  }, []);

  const value = useMemo<OperatorsAuthContextValue>(
    () => ({
      error,
      expiresAt: sessionExpiresAt,
      logout,
      signInWithDevSession,
      signInWithGoogleIdToken,
      status,
      user,
    }),
    [error, logout, sessionExpiresAt, signInWithDevSession, signInWithGoogleIdToken, status, user],
  );

  return <OperatorsAuthContext.Provider value={value}>{children}</OperatorsAuthContext.Provider>;
}

export function useOperatorsAuth(): OperatorsAuthContextValue {
  const context = useContext(OperatorsAuthContext);
  if (!context) {
    throw new Error("OperatorsAuthProvider is required");
  }

  return context;
}
